import { useRef, useState } from "react";
import { motion, useMotionValue, useTransform, useScroll } from "framer-motion";
import { ArrowUpRight, TrendingUp } from "lucide-react";
import mascot from "../assets/mascot.webp";
import mascotSm from "../assets/mascot-600.webp";
import { scrollToId, EASE } from "./helpers";

function MaskLine({ children, delay, className = "" }) {
    return (
        <span className="block overflow-hidden pb-1">
            <motion.span
                className={`block ${className}`}
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.55, delay, ease: EASE }}
            >
                {children}
            </motion.span>
        </span>
    );
}

export default function Hero() {
    const ref = useRef(null);
    const [isMobile] = useState(
        () =>
            typeof window !== "undefined" &&
            window.matchMedia("(max-width: 768px)").matches
    );
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const px = useTransform(mx, [-0.5, 0.5], [-20, 20]);
    const py = useTransform(my, [-0.5, 0.5], [-16, 16]);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const driftY = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

    const onMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    return (
        <section
            id="top"
            ref={ref}
            onMouseMove={onMove}
            className="relative flex min-h-[100svh] items-center overflow-hidden pt-24 pb-16"
        >
            {/* backdrop */}
            <div className="starfield mobile-glow absolute inset-0" aria-hidden="true" />
            <div
                className="absolute -top-40 left-[-10%] h-[560px] w-[560px] rounded-full bg-blast-pink/15 blur-[140px] max-sm:hidden"
                aria-hidden="true"
            />
            <div
                className="absolute bottom-[-20%] right-[-10%] h-[600px] w-[600px] rounded-full bg-blast-violet/10 blur-[160px] max-sm:hidden"
                aria-hidden="true"
            />

            <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1.15fr_0.85fr]">
                {/* copy */}
                <motion.div style={isMobile ? undefined : { opacity: fade }}>
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.45, delay: 0.02, ease: EASE }}
                        className="mb-8 inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/5 px-4 py-2 font-mono text-[10px] tracking-[0.3em] text-white/60"
                    >
                        <span className="blink h-2 w-2 rounded-full bg-blast-pink" />
                        PRESALE SOON — CA DROPS AFTER LAUNCH
                    </motion.div>

                    <h1 className="font-display text-[clamp(2.9rem,9vw,7rem)] font-black uppercase leading-[0.98]">
                        <MaskLine delay={0.05}>No brakes.</MaskLine>
                        <MaskLine delay={0.15}>
                            <span>
                                Just{" "}
                                <span className="bg-gradient-to-r from-blast-pink via-blast-rose to-blast-violet bg-clip-text text-transparent">
                                    blast.
                                </span>
                            </span>
                        </MaskLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2, ease: EASE }}
                        className="mt-7 max-w-md text-base leading-relaxed text-white/60 sm:text-lg"
                    >
                        $BLASTARDIO is the intergalactic memecoin steered by a
                        rogue asteroid with anger issues and a helmet — presale
                        dropping soon. Buckle up, there is no buckle.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.3, ease: EASE }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-buy-button"
                            onClick={() => scrollToId("#how-to-buy")}
                            className="group flex items-center gap-2 rounded-full bg-blast-pink px-8 py-4 font-mono text-xs font-bold tracking-[0.15em] text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_48px_rgba(255,46,147,0.55)]"
                        >
                            JOIN PRESALE
                            <ArrowUpRight
                                size={16}
                                className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                            />
                        </button>

                        <button
                            data-testid="hero-chart-button"
                            onClick={() => scrollToId("#chart")}
                            className="flex items-center gap-2 rounded-full border border-blast-violet/40 px-8 py-4 font-mono text-xs font-bold tracking-[0.15em] text-blast-violet transition-all hover:-translate-y-0.5 hover:bg-blast-violet/10 hover:shadow-[0_0_40px_rgba(176,38,255,0.25)]"
                        >
                            <TrendingUp size={16} />
                            VIEW CHART
                        </button>
                    </motion.div>

                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.45 }}
                        className="mt-8 font-mono text-[10px] tracking-[0.25em] text-white/35"
                    >
                        STAGE — PRESALE SOON &nbsp;·&nbsp; CA — TBA
                        &nbsp;·&nbsp; NETWORK — SUI
                    </motion.p>
                </motion.div>

                {/* mascot */}
                <motion.div
                    style={isMobile ? undefined : { y: driftY }}
                    className="relative mx-auto w-full max-w-[420px] lg:max-w-[520px]"
                >
                    <div
                        className="absolute inset-0 scale-110 rounded-full bg-[radial-gradient(circle,rgba(255,46,147,0.28),transparent_65%)] blur-2xl max-sm:hidden"
                        aria-hidden="true"
                    />
                    <div
                        className="absolute inset-[-4%] animate-spin-slow rounded-full border border-dashed border-white/15 max-sm:hidden"
                        aria-hidden="true"
                    />
                    <motion.div
                        style={{ x: px, y: py }}
                        initial={{ opacity: 0, scale: 0.85 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
                        className="floaty relative"
                    >
                        <img
                            src={mascotSm}
                            srcSet={`${mascotSm} 640w, ${mascot} 1254w`}
                            sizes="(max-width: 640px) 88vw, 520px"
                            alt="BLASTARDIO asteroid mascot"
                            loading="eager"
                            decoding="async"
                            className="w-full rounded-full mix-blend-screen max-sm:mix-blend-normal"
                            data-testid="hero-mascot-image"
                        />
                    </motion.div>
                    <div className="floaty absolute left-0 top-[16%] rounded-full border border-blast-pink/50 bg-[#0D040B]/70 px-4 py-2 font-mono text-[10px] tracking-[0.25em] text-blast-pink backdrop-blur max-sm:backdrop-blur-none" style={{ animationDelay: "-2s" }}>
                        100% MEME
                    </div>
                    <div className="floaty absolute bottom-[14%] right-0 rounded-full border border-blast-violet/50 bg-[#0D040B]/70 px-4 py-2 font-mono text-[10px] tracking-[0.25em] text-blast-violet backdrop-blur max-sm:backdrop-blur-none" style={{ animationDelay: "-4s" }}>
                        0 BRAKES
                    </div>
                </motion.div>
            </div>

            {/* scroll cue */}
            <div className="absolute bottom-7 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-3">
                <span className="font-mono text-[9px] tracking-[0.4em] text-white/40">
                    SCROLL
                </span>
                <span className="scroll-cue-line block h-10 w-px bg-gradient-to-b from-blast-pink to-transparent" />
            </div>
        </section>
    );
}
